import type { CatalogMap } from "./catalog";
import { addressFromRC } from "../rules/audit/addressing";
import type { SheetModel, WorkbookModel } from "../model";
import { Origin } from "../model";

/**
 * One function used in the workbook that doesn't appear in the catalog.
 * `examples` shows up to a few cells/formulas that reference it so the
 * user can recognise what they're looking at.
 */
export interface UncataloguedFunction {
  name: string;
  count: number;
  examples: Array<{ sheetName: string; address: string; formula: string }>;
}

const MAX_EXAMPLES_PER_FN = 3;

/**
 * Walk every formula cell in the scanned sheets, extract each function
 * call name, and return the ones the catalog doesn't know about. Results
 * are sorted by use-count descending, then alphabetically.
 *
 * Iterates the model's formula cells directly via `formulaA1Ids` and
 * the workbook-wide `formulaA1Strings` interning.
 *
 * Could dedupe by formulaA1Id and walk each unique formula only once,
 * but examples need to point to specific (sheet, address) cells so
 * the outer per-cell loop has to happen regardless. The per-formula
 * char-stream walk is dwarfed by the loop overhead in practice.
 */
export function findUncataloguedFunctions(
  sheets: SheetModel[],
  model: WorkbookModel,
  catalog: CatalogMap
): UncataloguedFunction[] {
  const counts = new Map<string, number>();
  const examples = new Map<string, UncataloguedFunction["examples"]>();
  const formulaA1Strings = model.catalogs.formulaA1Strings;

  for (const sheet of sheets) {
    const { rows, cols, startRow, startCol } = sheet.bounds;
    const cellOrigin = sheet.cellOrigin;
    const formulaA1Ids = sheet.formulaA1Ids;

    for (let r = 0; r < rows; r++) {
      const rowBase = r * cols;
      for (let c = 0; c < cols; c++) {
        const idx = rowBase + c;
        if (cellOrigin[idx] !== Origin.FORMULA) continue;
        const fId = formulaA1Ids[idx];
        if (fId < 0) continue;

        const formula = formulaA1Strings[fId];
        const names = extractFunctionNames(formula);
        if (names.size === 0) continue;

        const address = addressFromRC(startRow + r, startCol + c);
        for (const name of Array.from(names)) {
          if (catalog.has(name)) continue;
          counts.set(name, (counts.get(name) ?? 0) + 1);
          let ex = examples.get(name);
          if (!ex) {
            ex = [];
            examples.set(name, ex);
          }
          if (ex.length < MAX_EXAMPLES_PER_FN) {
            ex.push({ sheetName: sheet.name, address, formula });
          }
        }
      }
    }
  }

  const out: UncataloguedFunction[] = [];
  for (const [name, count] of Array.from(counts)) {
    out.push({ name, count, examples: examples.get(name) ?? [] });
  }
  out.sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  return out;
}

/**
 * Extract uppercase function-call names from a formula. Skips contents of
 * string literals (`"..."` with `""` as an escaped quote) and quoted sheet
 * names (`'...'` with `''` as an escaped quote).
 *
 * A function call is recognised as `NAME(` where NAME starts with a letter
 * or underscore and continues with letters / digits / dots / underscores.
 * Whitespace between the name and the opening paren is allowed.
 */
function extractFunctionNames(formula: string): Set<string> {
  const names = new Set<string>();
  const f = formula.startsWith("=") ? formula.slice(1) : formula;
  const len = f.length;
  let i = 0;

  while (i < len) {
    const c = f[i];

    if (c === '"') {
      i = skipPaired(f, i, '"');
      continue;
    }
    if (c === "'") {
      i = skipPaired(f, i, "'");
      continue;
    }

    if (/[A-Za-z_]/.test(c)) {
      let j = i + 1;
      while (j < len && /[A-Za-z0-9._]/.test(f[j])) j++;
      // Allow whitespace between name and "("
      let k = j;
      while (k < len && /\s/.test(f[k])) k++;
      if (f[k] === "(") {
        const name = f.slice(i, j).toUpperCase();
        names.add(name);
        i = k + 1;
        continue;
      }
      // Not a function call — could be a sheet-qualified reference or a
      // defined name. Advance past the identifier so we don't re-examine it.
      i = j;
      continue;
    }

    i++;
  }
  return names;
}

/**
 * Advance past a `"..."` or `'...'` span, treating a doubled quote (`""` or
 * `''`) as an escaped quote rather than the closing delimiter. Returns the
 * index just past the closing delimiter (or end-of-string if unterminated).
 */
function skipPaired(f: string, startIdx: number, quote: string): number {
  let i = startIdx + 1;
  const len = f.length;
  while (i < len) {
    if (f[i] === quote) {
      if (f[i + 1] === quote) {
        i += 2;
        continue;
      }
      return i + 1;
    }
    i++;
  }
  return i;
}
